/* ══════════════════════════════════════════════════════════════════
   DIAMOND SWIMWEAR — Master JavaScript
   Modules: Announcement Bar, Sticky Header, Mobile Nav, Search,
            Toast, Quick-Add (AJAX cart), Wishlist, Cart Badge
══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── HELPERS ──────────────────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ── TOAST ────────────────────────────────────────────────────── */
  const Toast = (() => {
    let el = null;
    let timer = null;

    function ensure() {
      if (el) return;
      el = document.createElement('div');
      el.className = 'dw-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }

    function show(msg, duration = 3000) {
      ensure();
      el.textContent = msg;
      el.classList.add('is-visible');
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('is-visible'), duration);
    }

    return { show };
  })();

  /* ── ANNOUNCEMENT BAR ─────────────────────────────────────────── */
  function initAnnounceBar() {
    const bar = $('.dw-announce');
    if (!bar) return;

    const slides = $$('.dw-announce__slide', bar);
    if (slides.length < 2) {
      if (slides[0]) slides[0].classList.add('is-active');
      return;
    }

    let current = 0;
    let intervalId = null;
    const interval = parseInt(bar.dataset.interval || '4000', 10);

    function show(idx) {
      slides[current].classList.remove('is-active');
      slides[current].classList.add('is-exiting');
      const prev = current;
      setTimeout(() => slides[prev].classList.remove('is-exiting'), 500);
      current = (idx + slides.length) % slides.length;
      slides[current].classList.add('is-active');
    }

    function start() {
      intervalId = setInterval(() => show(current + 1), interval);
    }

    function stop() {
      clearInterval(intervalId);
    }

    slides[0].classList.add('is-active');
    start();

    const prevBtn = $('.dw-announce__nav--prev', bar);
    const nextBtn = $('.dw-announce__nav--next', bar);
    if (prevBtn) prevBtn.addEventListener('click', () => { stop(); show(current - 1); start(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { stop(); show(current + 1); start(); });

    const closeBtn = $('.dw-announce__close', bar);
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        stop();
        bar.style.transition = 'opacity .3s, transform .3s';
        bar.style.opacity = '0';
        bar.style.transform = 'translateY(-100%)';
        setTimeout(() => {
          bar.remove();
          document.documentElement.style.setProperty('--announce-h', '0px');
        }, 320);
      });
    }
  }

  /* ── STICKY HEADER ────────────────────────────────────────────── */
  function initStickyHeader() {
    const header = $('.dw-header');
    const catNav = $('.dw-cat-nav');
    if (!header) return;

    const threshold = 50;

    function update() {
      const scrolled = window.scrollY > threshold;
      header.classList.toggle('is-scrolled', scrolled);
      if (catNav) catNav.classList.toggle('is-scrolled', scrolled);
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── MOBILE NAV ───────────────────────────────────────────────── */
  function initMobileNav() {
    const nav = $('.dw-mobile-nav');
    const hamburger = $('.dw-header__hamburger');
    if (!nav || !hamburger) return;

    const overlay = $('.dw-mobile-nav__overlay', nav);

    function open() {
      nav.classList.add('is-open');
      hamburger.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nav-open');
    }

    function close() {
      nav.classList.remove('is-open');
      hamburger.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('nav-open');
    }

    hamburger.addEventListener('click', () => {
      nav.classList.contains('is-open') ? close() : open();
    });

    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
    });

    /* Accordion sub-menus */
    $$('.dw-mobile-nav__accordion', nav).forEach(btn => {
      const sub = btn.closest('.dw-mobile-nav__item')?.querySelector('.dw-mobile-nav__sub');
      if (!sub) return;
      sub.hidden = true;
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        sub.hidden = expanded;
      });
    });
  }

  /* ── SEARCH OVERLAY ───────────────────────────────────────────── */
  function initSearch() {
    const overlay = $('.dw-search');
    if (!overlay) return;

    const input = $('.dw-search__input', overlay);
    const openBtns = $$('[data-search-open]');
    const closeBtns = $$('[data-search-close], .dw-search__close', overlay);
    const backdrop = $('.dw-search__backdrop', overlay);

    function open() {
      overlay.classList.add('is-open');
      document.body.classList.add('nav-open');
      setTimeout(() => input?.focus(), 100);
    }

    function close() {
      overlay.classList.remove('is-open');
      document.body.classList.remove('nav-open');
    }

    openBtns.forEach(b => b.addEventListener('click', open));
    closeBtns.forEach(b => b.addEventListener('click', close));
    backdrop?.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  }

  /* ── AJAX QUICK-ADD ───────────────────────────────────────────── */
  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn || btn.disabled) return;

      const variantId = btn.dataset.variantId;
      const title = btn.dataset.productTitle || 'Producto';
      if (!variantId) return;

      btn.disabled = true;
      btn.classList.add('is-adding');
      const original = btn.textContent.trim();
      btn.textContent = '...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        Toast.show(`✦ ${title} añadido`);
        updateCartCount();
      } catch (err) {
        Toast.show('Error al añadir. Intenta de nuevo.');
        console.error('[DW] Quick-add error:', err);
      } finally {
        btn.textContent = original;
        btn.classList.remove('is-adding');
        btn.disabled = false;
      }
    });
  }

  /* ── CART BADGE UPDATE ────────────────────────────────────────── */
  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const cart = await res.json();
      const count = cart.item_count || 0;

      $$('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.setAttribute('data-cart-count', count);
      });

      if (window.DWTheme) window.DWTheme.cartItemCount = count;
    } catch (e) {
      // silent
    }
  }

  /* ── WISHLIST TOGGLE ──────────────────────────────────────────── */
  function initWishlist() {
    const STORAGE_KEY = 'dw_wishlist';

    function getList() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      } catch {
        return [];
      }
    }

    function saveList(list) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    }

    function syncButtons() {
      const list = getList();
      $$('[data-wish]').forEach(btn => {
        const handle = btn.dataset.productHandle;
        const active = list.includes(handle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wish]');
      if (!btn) return;
      const handle = btn.dataset.productHandle;
      if (!handle) return;

      const list = getList();
      const idx = list.indexOf(handle);
      if (idx >= 0) {
        list.splice(idx, 1);
        Toast.show('Eliminado de favoritos');
      } else {
        list.push(handle);
        Toast.show('✦ Guardado en favoritos');
      }
      saveList(list);
      syncButtons();
    });

    syncButtons();
  }

  /* ── SMOOTH SCROLL FOR ANCHOR LINKS ──────────────────────────── */
  function initSmoothScroll() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--offset') || '148', 10);
      const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  /* ── INIT ─────────────────────────────────────────────────────── */
  function init() {
    initAnnounceBar();
    initStickyHeader();
    initMobileNav();
    initSearch();
    initQuickAdd();
    initWishlist();
    initSmoothScroll();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
